import type {
  BlogCategory,
  BlogGameRef,
  BlogPost,
  BlogTag,
} from '../../../shared/models/blog.model';

/**
 * Formas que devuelve `BlogService` (B59 del spec del blog). Son las que
 * consumen las páginas y los resolvers; `blog.mappers.ts` traduce a ellas el
 * payload público de la API (B119).
 */

/** Filtros del listado. Los cuatro se combinan con AND (B50). */
export interface BlogListFilters {
  game?: string;
  category?: string;
  tag?: string;
  search?: string;
}

/**
 * Eco de los filtros que el servidor ha RESUELTO (B72, `applied` de B84). Un
 * slug inexistente, inactivo o borrado (criterio de B30) no aparece: así el
 * SEO y las migas del listado (B66, B67) saben si el filtro existe y cómo se
 * llama, sin una segunda llamada a `taxonomies()`.
 */
export interface BlogAppliedFilters {
  game?: BlogGameRef;
  category?: BlogCategory;
  tag?: BlogTag;
  search?: string;
}

/** Una página del listado. El destacado viaja fuera de `posts` (B10). */
export interface BlogListPage {
  posts: BlogPost[];
  featured: BlogPost | null;
  /**
   * `meta.total` de la API: cuenta la rejilla paginada, SIN el destacado
   * cuando viaja aparte (E8). Con filtros no hay destacado y es exacto; sin
   * filtros el contador no se pinta (B131).
   */
  total: number;
  page: number;
  totalPages: number;
  applied: BlogAppliedFilters;
}

/**
 * Ficha de un artículo con su navegación: la forma de `GET
 * /web/blog/posts/{slug}` (B85). `post.slug` es el CANÓNICO que manda la API,
 * que puede no ser el pedido si el slug está retirado (301, B122).
 *
 * Contrato de `post` (B36, B69, B71): `relatedProducts` trae como mucho 4, en
 * el orden del pivot y solo publicables; `inlineProduct` se omite si no es
 * publicable; `relatedStoreCategories[].slug` es de un solo segmento.
 */
export interface BlogArticleView {
  post: BlogPost;
  /** Artículo anterior en el orden canónico (el más reciente de los dos). */
  prev: BlogPost | null;
  /** Artículo siguiente en el orden canónico (el más antiguo de los dos). */
  next: BlogPost | null;
  /** Hasta 3 del mismo juego, completados con los más recientes (B85). */
  related: BlogPost[];
}

/** Opciones de la barra de filtros. */
export interface BlogTaxonomies {
  games: BlogGameRef[];
  categories: BlogCategory[];
  tags: BlogTag[];
}

/**
 * Centinela de «la API no respondió» (B123): lo devuelven los resolvers en
 * lugar de propagar el error, que en SSR acabaría en el 404 genérico de
 * Express y en cliente dejaría la navegación en la página anterior.
 */
export const BLOG_LOAD_ERROR = { loadError: true } as const;
export type BlogLoadError = typeof BLOG_LOAD_ERROR;

export function isBlogLoadError(value: unknown): value is BlogLoadError {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { loadError?: unknown }).loadError === true
  );
}
